import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { Icon } from 'components';
import WalkthroughTooltip, {
  WalkthroughScrollType,
  WalkthroughTooltipType,
  DashboardWalkthroughProgressCount,
} from 'components/WalkthroughTooltip';
import TouchpointCard from 'components/candidate/TouchpointCard';
import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import { FLOW } from 'hooks/useApp';
import { useResponsive } from 'hooks/useResponsive';
import { ManagerFilterType } from 'pages/candidate/manager/Manager.component';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { MatchAttributes } from 'lib/models/matches';
import { TouchpointType } from 'lib/models/touchpoint';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import { useCandidateMatches } from 'lib/providers/CandidateMatchesProvider';
import { OpportunityAttributes } from 'lib/models/opportunity';
import ProgressBar from '../../../../components/ComponentV2/ProgressBar';

import styles from './MatchesBanner.module.scss';

export interface MatchesBannerProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  matchList: Array<MatchAttributes>;
  onClickPastMatches?: () => void;
  onOpenMatchingPreferencesModal: () => void;
  onOpenOnboarding?: () => void;
  isNonOnboardedUser: boolean;
}

export const MatchesBanner: React.FC<MatchesBannerProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    matchList,
    onClickPastMatches,
    onOpenMatchingPreferencesModal,
    isNonOnboardedUser,
  }: MatchesBannerProps) => {
    const router = useRouter();
    const screen = useResponsive();
    const [t] = useTranslation('candidate-dashboard');
    const contentClassNames = cn(
      {
        [styles.MatchesBanner]: true,
      },
      className
    );

    const changePrefrencesButton = useRef<HTMLButtonElement>(null);
    const navigateToMatchesPage = () =>
      router.push(PAGE_ROUTES.CANDIDATE_MATCHES);

    const [seenAllIslands, setSeenAllIslands] = useState<
      Array<{
        position: number;
        content: JSX.Element;
        show: boolean;
      }>
    >([
      {
        position: 1,
        content: (
          <div className={styles.island} onClick={onClickPastMatches}>
            <Icon
              key={1}
              iconName="icon_restore"
              className={styles.islandIcon}
            />
            <p className={styles.islandText}>
              {t('matches-banner.seen-all-opportunities.check-past-matches-1')}
              <span>
                {t(
                  'matches-banner.seen-all-opportunities.check-past-matches-2'
                )}
              </span>
            </p>
          </div>
        ),
        show: false,
      },
      {
        position: 2,
        content: (
          <div
            className={styles.island}
            onClick={() =>
              router.push(
                `${PAGE_ROUTES.MANAGER}?status=${ManagerFilterType.Saved}`
              )
            }
          >
            <Icon
              key={2}
              iconName="icon_bookmark"
              className={styles.islandIcon}
            />
            <p className={styles.islandText}>
              {t('matches-banner.seen-all-opportunities.review-saved-1')}
              <span>
                {t('matches-banner.seen-all-opportunities.review-saved-2')}
              </span>
            </p>
          </div>
        ),
        show: false,
      },
      {
        position: 3,
        content: (
          <div className={styles.island}>
            <Icon
              key={3}
              iconName="icon_profile"
              className={styles.islandIcon}
            />
            <p className={styles.islandText}>
              {t('matches-banner.seen-all-opportunities.complete-profile-1')}
              <span>
                {t('matches-banner.seen-all-opportunities.complete-profile-2')}
              </span>
            </p>
          </div>
        ),
        show: false,
      },
      {
        position: 4,
        content: (
          <div
            className={styles.island}
            onClick={() => router.push(PAGE_ROUTES.SEARCH_RESULTS)}
          >
            <Icon
              key={4}
              iconName="icon_search"
              className={styles.islandIcon}
            />
            <p className={styles.islandText}>
              {t('matches-banner.seen-all-opportunities.explore-other-opp-1')}
              <span>
                {t('matches-banner.seen-all-opportunities.explore-other-opp-2')}
              </span>
            </p>
          </div>
        ),
        show: true,
      },
      {
        position: 5,
        content: (
          <div className={styles.island}>
            <Icon key={5} iconName="icon_blog" className={styles.islandIcon} />
            <p className={styles.islandText}>
              <span>
                {t('matches-banner.seen-all-opportunities.check-blog-1')}
              </span>
              {t('matches-banner.seen-all-opportunities.check-blog-2')}
            </p>
          </div>
        ),
        show: true,
      },
    ]);
    const { candidateDashboard, candidateLastUpdatedStep } =
      useCandidateDashboard();
    const { matchesMeta, generateNewMatches } = useCandidateMatches();
    const { unvisited_count, total_count } = matchesMeta || {};
    const { saved_application_count, profile_complete, past_matches_present } =
      candidateDashboard || {};

    useEffect(() => {
      if (saved_application_count) {
        setSeenAllIslands((islands) => {
          return islands.map((item) => {
            if (item.position === 2) {
              return { ...item, show: true };
            } else return item;
          });
        });
      } else {
        setSeenAllIslands((islands) => {
          return islands.map((item) => {
            if (item.position === 2) {
              return { ...item, show: false };
            } else return item;
          });
        });
      }
    }, [saved_application_count]);

    useEffect(() => {
      if (past_matches_present) {
        setSeenAllIslands((islands) => {
          return islands.map((item) => {
            if (item.position === 1) {
              return { ...item, show: true };
            } else return item;
          });
        });
      }
    }, [past_matches_present]);

    useEffect(() => {
      if (profile_complete) {
        setSeenAllIslands((islands) => {
          return islands.map((item) => {
            if (item.position === 3) {
              return { ...item, show: false };
            } else return item;
          });
        });
      }
    }, [profile_complete]);

    const seenAllIslandsSection = useMemo(() => {
      return seenAllIslands
        .filter((island) => island.show)
        .slice(0, 3)
        .map((island, index) => {
          const { content } = island;
          return <React.Fragment key={index}>{content}</React.Fragment>;
        });
    }, [seenAllIslands]);

    const noOfSavedMatches = useMemo(() => {
      return matchList.filter((item) => item.saved_at).length;
    }, [matchList]);

    const getTouchpointCard = (
      touchpoints: Array<MatchAttributes>,
      isNewSet?: boolean
    ) => {
      const opportunities = touchpoints
        .slice(0)
        .sort((a, b) => {
          if (a.viewed_at === null && b.viewed_at !== null) return -1;
          if (a.viewed_at !== null && b.viewed_at === null) return 1;
          return 0; // Maintain the original order for equal viewed_at values
        })
        .slice(0, 3);
      return (
        <div className={styles.touchpointcardList}>
          {opportunities
            .slice(0, unvisited_count)
            ?.map(
              ({
                id,
                title,
                start_date,
                creatable_for,
                nested_type,
                matchable_slug,
                application_deadline,
                pay_currency,
                cohost_applications: cohostApplicationsResponse,
                touchpoint,
              }) => {
                const createdBy = parseResponse<
                  CompanyAttributes | StudentSocietyAttributes
                >(creatable_for);
                const { name, icon_url, is_verified, huzzle_verified } =
                  createdBy;
                const opportunity =
                  parseResponse<OpportunityAttributes>(touchpoint);
                const url = `${PAGE_ROUTES.CANDIDATE_MATCHES}/${matchable_slug}`;

                const cohost_applications = parseArrayResponse(
                  cohostApplicationsResponse
                );

                return (
                  <TouchpointCard
                    key={id}
                    icon_url={icon_url}
                    title={title}
                    createdBy={name}
                    startDate={start_date}
                    is_eligible_for_auto_apply={
                      opportunity.is_eligible_for_auto_apply
                    }
                    created_at={''}
                    application_deadline={application_deadline}
                    is_verified={is_verified}
                    touchpointable_type={nested_type as TouchpointType}
                    pay_currency={pay_currency || 'GBP'}
                    isExpired={false}
                    url={url}
                    className={styles.touchpointcard}
                    isNewSet={isNewSet || false}
                    huzzle_verified={huzzle_verified}
                    isOnboarding={true}
                    id={id}
                    slug={matchable_slug}
                    cities={null}
                    showManagerProgress={false}
                    cohost_applications={cohost_applications}
                    following={null}
                    apply_url={null}
                    extension_installed={true}
                  />
                );
              }
            )}
        </div>
      );
    };

    const getChangePreferencesButton = useCallback(
      ({ singleButton }: { singleButton: boolean }) => {
        return (
          <WalkthroughTooltip
            title={t('matches-banner.walkthrough.title')}
            description={t('matches-banner.walkthrough.desc')}
            buttonOneText={t('matches-banner.walkthrough.button')}
            walkthroughType={
              WalkthroughTooltipType.DASHBOARD_CHANGE_PREFERENCES
            }
            scrollToNextWalkthrough={
              WalkthroughScrollType.DASHBOARD_GENERATE_CV
            }
            scrollWalkthroughRef={changePrefrencesButton}
            position={screen.sm ? 'top' : 'right'}
            haveBoxShadow
            containerClassName={styles.wrapper}
            progressCount={
              DashboardWalkthroughProgressCount.DASHBOARD_TOTAL_STEPS
            }
            progressPosition={
              DashboardWalkthroughProgressCount.DASHBOARD_CHANGE_PREFERENCES
            }
          >
            <ButtonV3
              isFullWidth={singleButton}
              color={singleButton ? 'primary' : 'secondary'}
              className={cn(styles.matchesButton, {
                [styles.borderless]: singleButton,
              })}
              ref={changePrefrencesButton}
              onClick={() =>
                router.push(
                  `${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true`
                )
              }
            >
              {t('matches-banner.change-preferences')}
            </ButtonV3>
          </WalkthroughTooltip>
        );
      },
      [screen]
    );

    const percentCompleted = useMemo(() => {
      if (unvisited_count === undefined || total_count === undefined)
        return null;
      else
        return Math.round(
          ((total_count - unvisited_count) / total_count) * 100
        );
    }, [total_count, unvisited_count]);

    const banner = useMemo(() => {
      if (unvisited_count === undefined || total_count === undefined)
        return null;

      const defaultBanner = (
        <div className={cn(styles.heading)}>
          <div className={styles.headerWrapper}>
            <h1 className={styles.title}>
              {t('matches-banner.seen-all-opportunities.title')}
            </h1>
            <p className={styles.desc}>
              {t('matches-banner.seen-all-opportunities.desc')}
            </p>
          </div>
          <div className={cn(styles.islandSection, styles.gap)}>
            {seenAllIslandsSection}
          </div>
          <div className={styles['button-margin']}>
            {getChangePreferencesButton({ singleButton: true })}
          </div>
        </div>
      );

      if (
        (total_count === 0 &&
          (candidateLastUpdatedStep === FLOW.ONBOARDING ||
            candidateLastUpdatedStep === FLOW.MATCHING_PREFERENCES_SAVE)) ||
        candidateLastUpdatedStep === FLOW.PREVIOUS_MATCH_GENERATED ||
        candidateLastUpdatedStep === FLOW.PREVIOUS_MATCH_SET_COMPLETED ||
        candidateLastUpdatedStep === FLOW.MP_EMPTY_MATCH
      ) {
        return (
          <div className={cn(styles.heading, styles.headingProgress)}>
            <div className={styles.headerWrapper}>
              <h1 className={styles.title}>
                {t('matches-banner.no-opportunities-found.title')}
              </h1>
              <p className={cn(styles.desc)}>
                {t('matches-banner.no-opportunities-found.desc')}
              </p>
            </div>
            <div className={styles.gap}>
              {getChangePreferencesButton({ singleButton: true })}
            </div>
          </div>
        );
      } else if (candidateLastUpdatedStep === FLOW.SEEN_ALL_MACTHES) {
        return defaultBanner;
      } else if (unvisited_count === total_count && total_count > 0) {
        return (
          <div className={cn(styles.heading, styles.headingNew)}>
            <div className={styles.headerWrapper}>
              <h1 className={styles.title}>
                {t('matches-banner.new-matches.title', { unvisited_count })}
              </h1>
              <ProgressBar
                percentCompleted={
                  ((total_count - unvisited_count) / total_count) * 100
                }
                color="White"
                className={styles.bar}
              />
              <p className={cn(styles.desc, styles.descWhite)}>
                {t('matches-banner.new-matches.desc')}
              </p>
            </div>
            {getTouchpointCard(matchList, true)}
            <div className={cn(styles.footer)}>
              <ButtonV3
                color="secondary"
                className={styles.matchesButton}
                onClick={navigateToMatchesPage}
              >
                {t('matches-banner.view-matches')}
              </ButtonV3>
              {getChangePreferencesButton({ singleButton: false })}
            </div>
          </div>
        );
      } else if (
        unvisited_count !== total_count &&
        unvisited_count !== 0 &&
        total_count > 0
      ) {
        return (
          <div className={cn(styles.heading, styles.headingProgress)}>
            <div className={styles.headerWrapper}>
              <h1 className={styles.title}>
                {t('matches-banner.matches-left.title', { unvisited_count })}
              </h1>
              <ProgressBar
                percentCompleted={
                  ((total_count - unvisited_count) / total_count) * 100
                }
                color="Primary"
                className={styles.bar}
              />
              <p className={styles.desc}>
                {t('matches-banner.matches-left.desc', { percentCompleted })}
              </p>
            </div>
            {getTouchpointCard(matchList)}
            <div className={cn(styles.footer)}>
              <ButtonV3
                color="primary"
                onClick={navigateToMatchesPage}
                className={styles.matchesButton}
              >
                {t('matches-banner.view-matches')}
              </ButtonV3>
              {getChangePreferencesButton({ singleButton: false })}
            </div>
          </div>
        );
      } else if (unvisited_count === 0 && total_count > 0) {
        return (
          <div className={styles.heading}>
            <div className={styles.headerWrapper}>
              <h1 className={styles.title}>
                {t('matches-banner.seen-all-matches.title')}
              </h1>
              <ProgressBar
                percentCompleted={
                  ((total_count - unvisited_count) / total_count) * 100
                }
                color="Primary"
                className={styles.bar}
              />
              <p className={styles.desc}>
                {t('matches-banner.seen-all-matches.desc')}
              </p>
            </div>
            {noOfSavedMatches ? (
              <Row
                align="center"
                wrap={false}
                className={cn(styles.descTextRow, styles.gap)}
              >
                <p>
                  {t(
                    'matches-banner.seen-all-matches.apply-save-opportunities',
                    {
                      noOfSavedMatches,
                    }
                  )}
                </p>
                <ButtonV3
                  className={styles['saved-button']}
                  onClick={() =>
                    router.push(`${PAGE_ROUTES.MANAGER}?status=Saved`)
                  }
                >
                  {t('matches-banner.seen-all-matches.view-saved')}
                </ButtonV3>
              </Row>
            ) : null}
            <div className={cn(styles.footer, styles['button-margin'])}>
              <ButtonV3
                color="secondary"
                onClick={generateNewMatches}
                className={styles.matchesButton}
              >
                {t('matches-banner.view-more-matches')}
              </ButtonV3>
              {getChangePreferencesButton({ singleButton: false })}
            </div>
          </div>
        );
      } else {
        return defaultBanner;
      }
    }, [
      unvisited_count,
      total_count,
      matchList,
      noOfSavedMatches,
      candidateLastUpdatedStep,
      screen,
      navigateToMatchesPage,
      onOpenMatchingPreferencesModal,
    ]);

    const newUserBanner = (
      <div className={styles.heading}>
        <p className={styles.title}>
          {t('matches-banner.new-user-banner.title')}
        </p>
        <p className={styles.desc}>
          {t('matches-banner.new-user-banner.desc')}
        </p>
        {getTouchpointCard(matchList)}
        <ButtonV3
          isFullWidth
          onClick={() => router.push(PAGE_ROUTES.CANDIDATE_ONBOARDING)}
        >
          {t('matches-banner.new-user-banner.button')}
        </ButtonV3>
      </div>
    );

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="MatchesBanner"
      >
        {isNonOnboardedUser ? newUserBanner : banner}
      </div>
    );
  }
);

MatchesBanner.displayName = 'MatchesBanner';
